import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Send, MessageSquare, ClipboardList, Loader2, Smile, FileDown, Mail, Plus, X, Baby, Pencil, Trash2 } from "lucide-react";
import { fmtDateTime } from "@/lib/dates";
import { toast } from "sonner";

const MOODS = ["happy", "tired", "fussy", "calm"];

// iter-81d — same closed-today logic used on /dashboard/attendance so
// families see the closure banner before they leave the house.
const OP_DAYS_BY_CENTER = { "mon-fri": [1,2,3,4,5], "sun-sat": [0,1,2,3,4,5,6], "sat-sun": [0,6] };

function ClosedTodayBanner() {
  const [operatingDays, setOperatingDays] = useState(null);
  const [holiday, setHoliday] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/center/closed-today")
      .then((r) => {
        if (cancelled) return;
        setOperatingDays((r.data?.operating_days || "mon-fri").toLowerCase());
        setHoliday(r.data?.holiday || null);
      })
      .catch(() => { if (!cancelled) setOperatingDays("mon-fri"); });
    return () => { cancelled = true; };
  }, []);
  if (!operatingDays) return null;
  const todayIdx = new Date().getDay();
  const open = new Set(OP_DAYS_BY_CENTER[operatingDays] || [1,2,3,4,5]);
  const isNonOpDay = !open.has(todayIdx);
  const isHoliday = !!holiday;
  if (!isNonOpDay && !isHoliday) return null;
  const label = operatingDays === "mon-fri" ? "Mon–Fri" : operatingDays === "sat-sun" ? "Sat–Sun" : "Sun–Sat";
  return (
    <div
      data-testid="parent-closed-today-banner"
      data-holiday={holiday || ""}
      className="rounded-2xl border-2 border-dashed border-red-300 bg-red-50/70 px-5 py-4 flex flex-wrap items-center gap-3 animate-pulse"
      role="status"
    >
      <span className="inline-grid place-items-center w-9 h-9 rounded-full bg-red-600 text-white font-black text-lg">✕</span>
      <div className="min-w-0">
        <div className="font-display text-lg font-semibold text-red-800">
          {isHoliday ? <>We&apos;re closed today — {holiday}</> : <>We&apos;re closed today</>}
        </div>
        <div className="text-xs text-red-700/90">
          {isHoliday
            ? <>Observed holiday. No drop-off today — check messages for updates.</>
            : <>Operating days: <strong>{label}</strong>. No drop-off today — check messages for updates.</>}
        </div>
      </div>
    </div>
  );
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

export default function ParentApp() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/children").then((r) => { setChildren(r.data); if (r.data.length) setSelected(r.data[0].id); }).finally(() => setLoading(false));
  }, []);

  const refreshReports = () => api.get(`/daily-reports?child_id=${selected}`).then((r) => setReports(r.data));
  const refreshMessages = () => api.get(`/messages?child_id=${selected}`).then((r) => setMessages(r.data));

  useEffect(() => {
    if (!selected) return;
    Promise.all([
      api.get(`/messages?child_id=${selected}`).then((r) => r.data),
      api.get(`/daily-reports?child_id=${selected}`).then((r) => r.data),
    ]).then(([m, r]) => { setMessages(m); setReports(r); });
  }, [selected]);

  const selectedChild = useMemo(() => children.find((c) => c.id === selected), [children, selected]);

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>;
  if (children.length === 0) return <div className="py-20 text-center text-stone-500">Add a child first.</div>;

  return (
    <div data-testid="parent-app-page" className="space-y-8">
      <ClosedTodayBanner />
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Parent App</div>
          <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">Messages & daily reports</h1>
          <p className="mt-2 text-stone-500">What parents see — and what staff can post.</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Child</Label>
          <Select value={selected || ""} onValueChange={setSelected}>
            <SelectTrigger data-testid="parent-child-select" className="mt-2 h-11 w-64"><SelectValue /></SelectTrigger>
            <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </header>

      <Tabs defaultValue="messages">
        <TabsList className="bg-stone-100 p-1 rounded-xl h-auto">
          <TabsTrigger value="messages" data-testid="tab-messages" className="data-[state=active]:bg-white rounded-lg px-4 py-2.5">
            <MessageSquare className="w-4 h-4 mr-1" /> Messages ({messages.length})
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports" className="data-[state=active]:bg-white rounded-lg px-4 py-2.5">
            <ClipboardList className="w-4 h-4 mr-1" /> Daily reports ({reports.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="messages" className="mt-6">
          <Messaging childId={selected} user={user} messages={messages} onSent={refreshMessages} />
        </TabsContent>
        <TabsContent value="reports" className="mt-6">
          <DailyReports
            child={selectedChild}
            user={user}
            reports={reports}
            onAdded={refreshReports}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Messaging({ childId, user, messages, onSent }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try { await api.post("/messages", { child_id: childId, body, to_role: "parent" }); setBody(""); toast.success("Sent"); onSent(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="messages-thread" className="rounded-2xl border border-stone-200 bg-white">
      <div className="p-6 max-h-[400px] overflow-y-auto space-y-4" data-testid="messages-thread-list">
        {messages.length === 0 ? <p className="text-center text-sm text-stone-500 py-10">No messages yet — start the conversation.</p> :
          [...messages].reverse().map((m) => (
            <div key={m.id} className={`flex ${m.from_role === "parent" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-md rounded-2xl px-4 py-3 ${m.from_role === "parent" ? "bg-emerald-700 text-white" : "bg-stone-100 text-stone-900"}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{m.from_name || m.sender_name}</div>
                <div className="mt-1 text-sm whitespace-pre-line">{m.body}</div>
                <div className="mt-1 text-[10px] opacity-60">{fmtDateTime(m.created_at || m.ts)}</div>
              </div>
            </div>
          ))}
      </div>
      <form onSubmit={send} className="border-t border-stone-200 p-4 flex gap-2" data-testid="message-form">
        <Input data-testid="message-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a message to the parent…" className="h-11" />
        <Button type="submit" data-testid="message-send-btn" disabled={busy || !body.trim()} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
          <Send className="w-4 h-4 mr-1" /> Send
        </Button>
      </form>
    </div>
  );
}

const EMPTY_QUICK = () => ({
  template: "quick", mood: "happy", naps_minutes: 0, meals: [], diapers_changes: 0, notes: "",
});

const EMPTY_DCY = () => ({
  template: "dcy_01228",
  report_date: todayIso(),
  notes_from_home: "",
  arrival_time: "",
  arrival_meridiem: "am",
  time_entries: [{ time: "", diapers: "", nap: "", bottle_oz: "" }],
  am_snack: "", am_snack_amount: "",
  lunch: "", lunch_amount: "",
  pm_snack: "", pm_snack_amount: "",
  additional_food: "", additional_food_amount: "",
  daily_activities: "",
  additional_information: "",
  // Quick fields kept compatible:
  mood: "happy", naps_minutes: 0, meals: [], diapers_changes: 0, notes: "",
});

function DailyReports({ child, user, reports, onAdded }) {
  const childId = child?.id;
  const [tmpl, setTmpl] = useState("dcy_01228");
  const [form, setForm] = useState(() => EMPTY_DCY());
  const [teacherName, setTeacherName] = useState(user?.name || "");
  const [mealInput, setMealInput] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset when switching template
  useEffect(() => {
    setForm(tmpl === "dcy_01228" ? EMPTY_DCY() : EMPTY_QUICK());
    setMealInput("");
  }, [tmpl]);

  // Default the reporting teacher to the logged-in user.
  useEffect(() => { if (!teacherName && user?.name) setTeacherName(user.name); }, [user, teacherName]);

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addMeal = () => { if (mealInput.trim()) { up("meals", [...(form.meals || []), mealInput.trim()]); setMealInput(""); } };

  const upEntry = (i, k, v) => setForm((f) => {
    const list = [...(f.time_entries || [])];
    list[i] = { ...list[i], [k]: v };
    return { ...f, time_entries: list };
  });
  const addEntry = () => setForm((f) => ({
    ...f,
    time_entries: [...(f.time_entries || []), { time: "", diapers: "", nap: "", bottle_oz: "" }],
  }));
  const removeEntry = (i) => setForm((f) => {
    const list = [...(f.time_entries || [])];
    list.splice(i, 1);
    return { ...f, time_entries: list.length ? list : [{ time: "", diapers: "", nap: "", bottle_oz: "" }] };
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        child_id: childId,
        reporting_teacher_name: teacherName || user?.name || "",
        ...form,
      };
      await api.post("/daily-reports", payload);
      toast.success("Daily report saved.");
      setForm(tmpl === "dcy_01228" ? EMPTY_DCY() : EMPTY_QUICK());
      setMealInput("");
      onAdded();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <form onSubmit={submit} data-testid="daily-report-form" className="lg:col-span-2 rounded-2xl border border-stone-200 bg-white p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display text-lg font-medium text-stone-900">Post today&apos;s report</h3>
          <Select value={tmpl} onValueChange={setTmpl}>
            <SelectTrigger data-testid="report-template-select" className="h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="dcy_01228"><Baby className="inline w-3 h-3 mr-1.5" />Infant (DCY 01228)</SelectItem>
              <SelectItem value="quick">Quick</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Reporting Teacher</Label>
          <Input
            data-testid="report-teacher-input"
            value={teacherName}
            onChange={(e) => setTeacherName(e.target.value)}
            placeholder="Teacher who completed this report"
            className="mt-2 h-11"
          />
          <p className="mt-1 text-[11px] text-stone-500">Defaults to the logged-in user. Stored with the record and printed on the PDF.</p>
        </div>

        {tmpl === "dcy_01228" ? (
          <DcyForm form={form} up={up} upEntry={upEntry} addEntry={addEntry} removeEntry={removeEntry} />
        ) : (
          <QuickForm form={form} up={up} mealInput={mealInput} setMealInput={setMealInput} addMeal={addMeal} />
        )}

        <Button type="submit" data-testid="report-submit" disabled={busy || !childId} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Post report"}
        </Button>
      </form>

      <div className="lg:col-span-3 space-y-4" data-testid="daily-reports-list">
        {reports.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-10 text-center text-sm text-stone-500">
            No daily reports yet.
          </div>
        ) : reports.map((r) => (
          <ReportCard
            key={r.id}
            report={r}
            child={child}
            onChanged={onAdded}
          />
        ))}
      </div>
    </div>
  );
}

function DcyForm({ form, up, upEntry, addEntry, removeEntry }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Report Date</Label>
          <Input data-testid="report-date" type="date" value={form.report_date} onChange={(e) => up("report_date", e.target.value)} className="mt-2 h-11" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Arrival Time</Label>
          <div className="flex gap-2 mt-2">
            <Input data-testid="report-arrival-time" value={form.arrival_time} onChange={(e) => up("arrival_time", e.target.value)} placeholder="8:30" className="h-11" />
            <Select value={form.arrival_meridiem} onValueChange={(v) => up("arrival_meridiem", v)}>
              <SelectTrigger data-testid="report-arrival-meridiem" className="h-11 w-20"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="am">am</SelectItem>
                <SelectItem value="pm">pm</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">Notes From Home</Label>
        <Textarea data-testid="report-notes-from-home" value={form.notes_from_home} onChange={(e) => up("notes_from_home", e.target.value)} rows={2} className="mt-2" />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-widest text-stone-500">Time-by-time log</Label>
          <Button type="button" data-testid="report-add-entry" onClick={addEntry} variant="outline" className="h-7 rounded-lg text-xs" disabled={(form.time_entries || []).length >= 12}>
            <Plus className="w-3 h-3 mr-1" /> Row
          </Button>
        </div>
        <div className="mt-2 space-y-2">
          {(form.time_entries || []).map((e, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-1.5 items-center">
              <Input data-testid={`entry-time-${i}`} placeholder="Time" value={e.time || ""} onChange={(ev) => upEntry(i, "time", ev.target.value)} className="h-9 text-xs" />
              <Input data-testid={`entry-diapers-${i}`} placeholder="Diapers" value={e.diapers || ""} onChange={(ev) => upEntry(i, "diapers", ev.target.value)} className="h-9 text-xs" />
              <Input data-testid={`entry-nap-${i}`} placeholder="Nap" value={e.nap || ""} onChange={(ev) => upEntry(i, "nap", ev.target.value)} className="h-9 text-xs" />
              <Input data-testid={`entry-bottle-${i}`} placeholder="Bottle oz" value={e.bottle_oz || ""} onChange={(ev) => upEntry(i, "bottle_oz", ev.target.value)} className="h-9 text-xs" />
              <Button type="button" onClick={() => removeEntry(i)} variant="ghost" className="h-9 w-9 p-0">
                <X className="w-3.5 h-3.5 text-stone-400" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <MealsRow label="AM Snack" name="am_snack" form={form} up={up} />
      <MealsRow label="Lunch" name="lunch" form={form} up={up} />
      <MealsRow label="PM Snack" name="pm_snack" form={form} up={up} />
      <MealsRow label="Additional Food" name="additional_food" form={form} up={up} />

      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">Daily Activities</Label>
        <Textarea data-testid="report-daily-activities" value={form.daily_activities} onChange={(e) => up("daily_activities", e.target.value)} rows={3} className="mt-2" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">Additional Information</Label>
        <Textarea data-testid="report-additional-info" value={form.additional_information} onChange={(e) => up("additional_information", e.target.value)} rows={2} className="mt-2" />
      </div>
    </>
  );
}

function MealsRow({ label, name, form, up }) {
  const amountKey = name === "am_snack" ? "am_snack_amount"
    : name === "lunch" ? "lunch_amount"
    : name === "pm_snack" ? "pm_snack_amount"
    : "additional_food_amount";
  return (
    <div className="grid grid-cols-[1fr_120px] gap-2">
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label>
        <Input data-testid={`report-${name}`} value={form[name] || ""} onChange={(e) => up(name, e.target.value)} className="mt-2 h-10 text-sm" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">Amount</Label>
        <Input data-testid={`report-${amountKey}`} value={form[amountKey] || ""} onChange={(e) => up(amountKey, e.target.value)} className="mt-2 h-10 text-sm" />
      </div>
    </div>
  );
}

function QuickForm({ form, up, mealInput, setMealInput, addMeal }) {
  return (
    <>
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">Mood</Label>
        <Select value={form.mood} onValueChange={(v) => up("mood", v)}>
          <SelectTrigger data-testid="report-mood" className="mt-2 h-11"><SelectValue /></SelectTrigger>
          <SelectContent>{MOODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Naps (min)</Label>
          <Input data-testid="report-naps" type="number" value={form.naps_minutes} onChange={(e) => up("naps_minutes", parseInt(e.target.value || 0))} className="mt-2 h-11" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Diaper changes</Label>
          <Input data-testid="report-diapers" type="number" value={form.diapers_changes} onChange={(e) => up("diapers_changes", parseInt(e.target.value || 0))} className="mt-2 h-11" />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">Meals</Label>
        <div className="flex gap-2 mt-2">
          <Input data-testid="report-meal-input" value={mealInput} onChange={(e) => setMealInput(e.target.value)} placeholder="e.g. Pasta + veggies" className="h-11" />
          <Button type="button" data-testid="report-meal-add" onClick={addMeal} variant="outline" className="rounded-xl">+</Button>
        </div>
        {form.meals.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {form.meals.map((m, i) => (
              <li key={i} className="text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200">{m}</li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">Notes</Label>
        <Textarea data-testid="report-notes" value={form.notes} onChange={(e) => up("notes", e.target.value)} rows={3} className="mt-2" />
      </div>
    </>
  );
}

function ReportCard({ report, child, onChanged }) {
  const [busyPdf, setBusyPdf] = useState(false);
  const [busyEmail, setBusyEmail] = useState(false);
  const [busyDelete, setBusyDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const teacher = report.reporting_teacher_name || report.author_name || "—";
  const isDcy = report.template === "dcy_01228";

  const downloadPdf = async () => {
    setBusyPdf(true);
    try {
      const res = await api.get(`/daily-reports/${report.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      downloadBlob(blob, `DCY01228-${child?.last_name || "report"}-${report.report_date || ""}.pdf`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyPdf(false); }
  };

  const emailParents = async () => {
    if (!child?.parent_email) { toast.error("No parent_email on file for this child."); return; }
    if (!window.confirm(`Email this daily report PDF to ${child.parent_email}?`)) return;
    setBusyEmail(true);
    try {
      await api.post(`/daily-reports/${report.id}/email`, { note: "" });
      toast.success(`Report emailed to ${child.parent_email}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyEmail(false); }
  };

  const deleteReport = async () => {
    if (!window.confirm("Delete this daily report? This cannot be undone.")) return;
    setBusyDelete(true);
    try {
      await api.delete(`/daily-reports/${report.id}`);
      toast.success("Report deleted.");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyDelete(false); }
  };

  return (
    <article data-testid={`report-card-${report.id}`} className="rounded-2xl border border-stone-200 bg-white p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-emerald-700">
          {isDcy ? <Baby className="w-3.5 h-3.5" /> : <Smile className="w-3.5 h-3.5" />}
          {isDcy ? "DCY 01228" : (report.mood || "report")}
        </div>
        <div className="text-[11px] text-stone-500 text-right">
          <div>{fmtDateTime(report.created_at)}</div>
          <div className="font-semibold text-stone-700">Teacher: {teacher}</div>
          {report.updated_at && (
            <div className="italic text-stone-400">edited {fmtDateTime(report.updated_at)} by {report.updated_by_name || "—"}</div>
          )}
        </div>
      </div>

      {isDcy ? (
        <DcyReportBody report={report} />
      ) : (
        <QuickReportBody report={report} />
      )}

      <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-stone-100">
        <Button
          type="button"
          data-testid={`report-pdf-${report.id}`}
          onClick={downloadPdf}
          disabled={busyPdf}
          variant="outline"
          className="rounded-xl text-xs"
        >
          {busyPdf ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
          Download PDF
        </Button>
        <Button
          type="button"
          data-testid={`report-email-${report.id}`}
          onClick={emailParents}
          disabled={busyEmail || !child?.parent_email}
          className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl text-xs"
          title={child?.parent_email ? `Email to ${child.parent_email}` : "No parent_email on file"}
        >
          {busyEmail ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
          Email to parents
        </Button>
        <div className="flex-1" />
        <Button
          type="button"
          data-testid={`report-edit-${report.id}`}
          onClick={() => setEditing(true)}
          variant="outline"
          className="rounded-xl text-xs"
        >
          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
        </Button>
        <Button
          type="button"
          data-testid={`report-delete-${report.id}`}
          onClick={deleteReport}
          disabled={busyDelete}
          variant="ghost"
          className="rounded-xl text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
        >
          {busyDelete ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
          Delete
        </Button>
      </div>

      <EditReportDialog
        open={editing}
        onOpenChange={setEditing}
        report={report}
        onSaved={() => { setEditing(false); onChanged?.(); }}
      />
    </article>
  );
}

function EditReportDialog({ open, onOpenChange, report, onSaved }) {
  const isDcy = report.template === "dcy_01228";
  const [form, setForm] = useState(() => ({ ...report, time_entries: report.time_entries || [], meals: report.meals || [] }));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...report, time_entries: report.time_entries || [], meals: report.meals || [] });
  }, [open, report]);

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const upEntry = (i, k, v) => setForm((f) => {
    const list = [...(f.time_entries || [])];
    list[i] = { ...list[i], [k]: v };
    return { ...f, time_entries: list };
  });
  const addEntry = () => setForm((f) => ({ ...f, time_entries: [...(f.time_entries || []), { time: "", diapers: "", nap: "", bottle_oz: "" }] }));
  const removeEntry = (i) => setForm((f) => {
    const list = [...(f.time_entries || [])];
    list.splice(i, 1);
    return { ...f, time_entries: list };
  });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = isDcy ? {
        report_date: form.report_date,
        reporting_teacher_name: form.reporting_teacher_name,
        notes_from_home: form.notes_from_home,
        arrival_time: form.arrival_time,
        arrival_meridiem: form.arrival_meridiem,
        time_entries: form.time_entries,
        am_snack: form.am_snack, am_snack_amount: form.am_snack_amount,
        lunch: form.lunch, lunch_amount: form.lunch_amount,
        pm_snack: form.pm_snack, pm_snack_amount: form.pm_snack_amount,
        additional_food: form.additional_food, additional_food_amount: form.additional_food_amount,
        daily_activities: form.daily_activities,
        additional_information: form.additional_information,
      } : {
        reporting_teacher_name: form.reporting_teacher_name,
        mood: form.mood,
        naps_minutes: form.naps_minutes,
        meals: form.meals,
        diapers_changes: form.diapers_changes,
        notes: form.notes,
      };
      await api.put(`/daily-reports/${report.id}`, payload);
      toast.success("Report updated.");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="report-edit-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit daily report</DialogTitle>
          <DialogDescription>{isDcy ? "DCY 01228 Infant Daily Report" : "Quick report"} · created {fmtDateTime(report.created_at)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-stone-500">Reporting Teacher</Label>
            <Input data-testid="edit-teacher" value={form.reporting_teacher_name || ""} onChange={(e) => up("reporting_teacher_name", e.target.value)} className="mt-1.5 h-10" />
          </div>
          {isDcy ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Report Date</Label>
                  <Input data-testid="edit-date" type="date" value={form.report_date || ""} onChange={(e) => up("report_date", e.target.value)} className="mt-1.5 h-10" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Arrival</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input value={form.arrival_time || ""} onChange={(e) => up("arrival_time", e.target.value)} placeholder="8:30" className="h-10" />
                    <Select value={form.arrival_meridiem || "am"} onValueChange={(v) => up("arrival_meridiem", v)}>
                      <SelectTrigger className="h-10 w-20"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="am">am</SelectItem><SelectItem value="pm">pm</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Notes From Home</Label>
                <Textarea data-testid="edit-notes-from-home" value={form.notes_from_home || ""} onChange={(e) => up("notes_from_home", e.target.value)} rows={2} className="mt-1.5" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Time-by-time log</Label>
                  <Button type="button" onClick={addEntry} variant="outline" className="h-7 rounded-lg text-xs" disabled={(form.time_entries || []).length >= 12}>
                    <Plus className="w-3 h-3 mr-1" /> Row
                  </Button>
                </div>
                <div className="mt-2 space-y-2">
                  {(form.time_entries || []).map((e, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-1.5 items-center">
                      <Input placeholder="Time" value={e.time || ""} onChange={(ev) => upEntry(i, "time", ev.target.value)} className="h-9 text-xs" />
                      <Input placeholder="Diapers" value={e.diapers || ""} onChange={(ev) => upEntry(i, "diapers", ev.target.value)} className="h-9 text-xs" />
                      <Input placeholder="Nap" value={e.nap || ""} onChange={(ev) => upEntry(i, "nap", ev.target.value)} className="h-9 text-xs" />
                      <Input placeholder="Bottle oz" value={e.bottle_oz || ""} onChange={(ev) => upEntry(i, "bottle_oz", ev.target.value)} className="h-9 text-xs" />
                      <Button type="button" onClick={() => removeEntry(i)} variant="ghost" className="h-9 w-9 p-0">
                        <X className="w-3.5 h-3.5 text-stone-400" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <MealsRow label="AM Snack" name="am_snack" form={form} up={up} />
              <MealsRow label="Lunch" name="lunch" form={form} up={up} />
              <MealsRow label="PM Snack" name="pm_snack" form={form} up={up} />
              <MealsRow label="Additional Food" name="additional_food" form={form} up={up} />
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Daily Activities</Label>
                <Textarea data-testid="edit-activities" value={form.daily_activities || ""} onChange={(e) => up("daily_activities", e.target.value)} rows={2} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Additional Information</Label>
                <Textarea value={form.additional_information || ""} onChange={(e) => up("additional_information", e.target.value)} rows={2} className="mt-1.5" />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Mood</Label>
                <Select value={form.mood || "happy"} onValueChange={(v) => up("mood", v)}>
                  <SelectTrigger className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{MOODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Naps (min)</Label>
                  <Input type="number" value={form.naps_minutes || 0} onChange={(e) => up("naps_minutes", parseInt(e.target.value || 0))} className="mt-1.5 h-10" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Diaper changes</Label>
                  <Input type="number" value={form.diapers_changes || 0} onChange={(e) => up("diapers_changes", parseInt(e.target.value || 0))} className="mt-1.5 h-10" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500">Notes</Label>
                <Textarea value={form.notes || ""} onChange={(e) => up("notes", e.target.value)} rows={3} className="mt-1.5" />
              </div>
            </>
          )}
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="edit-save-btn" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function QuickReportBody({ report }) {
  return (
    <>
      <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
        <Stat label="Naps" value={`${report.naps_minutes || 0}m`} />
        <Stat label="Diapers" value={report.diapers_changes || 0} />
        <Stat label="Meals" value={(report.meals || []).length} />
      </div>
      {(report.meals || []).length > 0 && <div className="mt-2 text-xs text-stone-600">Meals: {(report.meals || []).join(" · ")}</div>}
      {report.notes && <div className="mt-3 text-sm text-stone-700 leading-relaxed border-t border-stone-100 pt-3">{report.notes}</div>}
    </>
  );
}

function DcyReportBody({ report }) {
  const arrival = [report.arrival_time, report.arrival_meridiem].filter(Boolean).join(" ");
  const entries = report.time_entries || [];
  return (
    <div className="mt-3 space-y-2 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Report Date" value={report.report_date || "—"} />
        <Stat label="Arrival" value={arrival || "—"} />
      </div>
      {report.notes_from_home && (
        <div className="text-xs text-stone-600"><b>From home:</b> {report.notes_from_home}</div>
      )}
      {entries.length > 0 && (
        <div className="rounded-lg border border-stone-200 overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-stone-50 text-stone-500 uppercase tracking-wider">
              <tr><th className="px-2 py-1 text-left">Time</th><th className="px-2 py-1 text-left">Diaper</th><th className="px-2 py-1 text-left">Nap</th><th className="px-2 py-1 text-left">Bottle</th></tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i} className="border-t border-stone-100">
                  <td className="px-2 py-1">{e.time || "—"}</td>
                  <td className="px-2 py-1">{e.diapers || "—"}</td>
                  <td className="px-2 py-1">{e.nap || "—"}</td>
                  <td className="px-2 py-1">{e.bottle_oz || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="grid grid-cols-2 gap-1.5 text-[11px] text-stone-600">
        {report.am_snack && <div><b>AM:</b> {report.am_snack} <span className="text-stone-400">({report.am_snack_amount || "—"})</span></div>}
        {report.lunch && <div><b>Lunch:</b> {report.lunch} <span className="text-stone-400">({report.lunch_amount || "—"})</span></div>}
        {report.pm_snack && <div><b>PM:</b> {report.pm_snack} <span className="text-stone-400">({report.pm_snack_amount || "—"})</span></div>}
        {report.additional_food && <div><b>Other:</b> {report.additional_food} <span className="text-stone-400">({report.additional_food_amount || "—"})</span></div>}
      </div>
      {report.daily_activities && (
        <div className="text-xs text-stone-700 leading-relaxed border-t border-stone-100 pt-2"><b>Activities:</b> {report.daily_activities}</div>
      )}
      {report.additional_information && (
        <div className="text-xs text-stone-700 leading-relaxed"><b>Additional:</b> {report.additional_information}</div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-stone-50 px-3 py-2">
    <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
    <div className="font-display text-lg font-semibold text-stone-900">{value}</div>
  </div>
);
